import React, { createContext, useContext, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DatePicker } from '../common/DatePicker';
import styles from './ParamSheet.module.css';

/// Spreadsheet-style parameter editor: every parameter is one row of
/// `name | value`, and groups or dependent parameters nest as indented rows.

export const ParamSheet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.sheet}>
    <div className={styles.sheetHead}>
      <span>Parameter</span>
      <span>Value</span>
    </div>
    {children}
  </div>
);

interface ParamSectionProps {
  title: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export const ParamSection: React.FC<ParamSectionProps> = ({
  title,
  badge,
  actions,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <button type="button" className={styles.sectionToggle} onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          <span className={styles.sectionTitle}>{title}</span>
          {badge && <span className={styles.sectionBadge}>{badge}</span>}
        </button>
        {actions && <span className={styles.sectionActions}>{actions}</span>}
      </div>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </div>
  );
};

const NestContext = createContext(false);

/// Wraps the rows a parent parameter reveals; renders them indented.
export const ParamNest: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <NestContext.Provider value={true}>{children}</NestContext.Provider>
);

interface ParamRowProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

export const ParamRow: React.FC<ParamRowProps> = ({ label, hint, children }) => {
  const nested = useContext(NestContext);
  return (
    <div className={`${styles.row} ${nested ? styles.nested : ''}`}>
      <div className={styles.labelCell}>
        <span>{label}</span>
        {hint && <span className={styles.hint}>{hint}</span>}
      </div>
      <div className={styles.valueCell}>{children}</div>
    </div>
  );
};

export const SheetButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button type="button" className={styles.rowAction} onClick={onClick}>
    {children}
  </button>
);

export const IconButton: React.FC<{ onClick: () => void; label: string; children: React.ReactNode }> = ({
  onClick,
  label,
  children,
}) => (
  <button type="button" className={styles.iconBtn} onClick={onClick} aria-label={label}>
    {children}
  </button>
);

// ---- Value cells ----

export const TextCell: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => (
  <input
    className={styles.cellInput}
    type="text"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
  />
);

export const DateCell: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div className={styles.dateCell}>
    <DatePicker value={value} onChange={onChange} className={styles.cellInput} />
  </div>
);

export const NumberCell: React.FC<{
  value: number;
  onChange: (v: number) => void;
  step?: number | 'any';
  integer?: boolean;
}> = ({ value, onChange, step = 'any', integer = false }) => (
  <input
    className={`${styles.cellInput} ${styles.numCell}`}
    type="number"
    step={integer ? 1 : step}
    value={value}
    onChange={(e) => {
      const parsed = integer ? parseInt(e.target.value, 10) : parseFloat(e.target.value);
      onChange(Number.isFinite(parsed) ? parsed : 0);
    }}
  />
);

export const SelectCell = <T extends string>({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: T | '';
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
  placeholder?: string;
}) => (
  <select className={styles.cellInput} value={value} onChange={(e) => onChange(e.target.value as T)}>
    {placeholder && (
      <option value="" disabled>
        {placeholder}
      </option>
    )}
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

export const MonoCell: React.FC<{ text: string; error?: boolean }> = ({ text, error = false }) => (
  <span className={`${styles.monoCell} ${error ? styles.monoError : ''}`}>{text}</span>
);

export const CheckCell: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  onLabel?: string;
  offLabel?: string;
}> = ({ checked, onChange, onLabel = 'Yes', offLabel = 'No' }) => (
  <label className={styles.checkCell}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    {checked ? onLabel : offLabel}
  </label>
);

const TENOR_UNITS = [
  { value: 'd', label: 'Days' },
  { value: 'w', label: 'Weeks' },
  { value: 'm', label: 'Months' },
  { value: 'y', label: 'Years' },
];

/// A window is a tenor string like `3y` / `6m`; edited as a count plus a unit.
export const TenorCell: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const m = /^(\d+)\s*([dwmy])$/i.exec(value.trim());
  const count = m ? m[1] : '1';
  const unit = m ? m[2].toLowerCase() : 'y';
  const emit = (c: string, u: string) => onChange(`${Math.max(1, Math.floor(Number(c) || 1))}${u}`);
  return (
    <div className={styles.tenorCell}>
      <input
        className={`${styles.cellInput} ${styles.numCell} ${styles.tenorNum}`}
        type="number"
        min={1}
        step={1}
        value={count}
        onChange={(e) => emit(e.target.value, unit)}
      />
      <select className={styles.cellInput} value={unit} onChange={(e) => emit(count, e.target.value)}>
        {TENOR_UNITS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};
